import {
  findRoute2g,
  Route2g,
  upsertRoute2gToll,
} from "@/reducers/routes2gSlice"
import {
  memo,
  Ref,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import "maplibre-theme/icons.default.css"
import "maplibre-theme/classic.css"
import { isMobile } from "@/utils/detectDevice"
import ReferenceRouteManager from "./ReferenceRouteManager"
import ShowEntireRouteButton from "./ShowEntireRouteButton"
import ShowCurrentLocationButton from "./ShowCurrentLocationButton"
import ChangeMapStyle from "./ChangeMapStyle"
import OpenFullScreen from "./OpenFullScreen"
import css from "../RouteView.module.css"
import ScrollContainer from "./ScrollContainer"
import RouteOptionsSelect from "./RouteOptionsSelect"
import TollsInformation from "./TollsInformation"
import ReadOnlyRouteStopList from "./ReadOnlyRouteStopList"
import RouteStopList, {
  calculateRouteStopDiff,
  RouteStopDiff,
} from "../RouteStopList"
import { useSharedRouteSettings } from "@/hooks/useSharedRouteSettings"
import ShareRoute from "./ShareRoute"
import { RouteInfo } from "./RouteView"
import useDriverCurrentLocation from "@/hooks/useDriverCurrentLocation"
import CloseMobileMap from "@/components/maps/CloseMobileMap"
import { useDarkMode } from "@/contexts/DarkModeContext"
import MapManager, {
  MapStyle,
  PastStopsOverlay,
} from "@/components/maps/MapManager"
import { shouldShowEntireRouteOnUpdate } from "@/components/maps/shouldShowEntireRoute"
import { useFeatureFlagEnabled } from "posthog-js/react"
import RouteTrafficSwitch from "@/components/maps/RouteTrafficSwitch"
import MapButtonColumn from "@/components/maps/MapButtonColumn"
import {
  ROUTE_TRAFFIC_FLAG,
  ROUTE_TRAFFIC_OFF,
  isRouteTrafficMap,
} from "@/components/maps/routeTraffic"
import {
  countIncidentTypes,
  incidentsAlongRoute,
  routeIncidents,
} from "@/components/maps/trafficIncidents"
import { useRouteTrafficSettings } from "@/hooks/useRouteTrafficSettings"
import { DriverContext } from "@/contexts/DriverContext"
import { isSltExportEnabledVhost } from "@/lib/sltExportHostRules"
import AdHocCommands, {
  AvoidType,
  CarRouteConfig,
  LoadType,
} from "@/api/adHocCommands"
import AvoidanceItemsParser, {
  AvoidanceItems,
} from "@/lib/avoidanceItemsParser"
import WaypointContextMenu from "./WaypointContextMenu"
import WaypointDetailsMenu from "./WaypointDetailsMenu"
import { useMediaQuery } from "@react-hookz/web"
import { MapContext } from "@/components/maps/mapLibre/MapContext"
import Marker from "@/components/maps/mapLibre/Marker"
import useDriverCurrentActivity from "@/hooks/useDriverCurrentActivity"
import { useMapToaster } from "@/hooks/useMapToaster"
import { useRouteTolls } from "@/hooks/useRouteTolls"
import { TemperatureAlertSettings } from "./TemperatureAlertSettings"
import DeliveryOrderExportStatusCard from "./DeliveryOrderExportStatusCard"
import FuelStationsSwitch from "@/components/maps/FuelStationsSwitch"
import AutohofsSwitch from "@/components/maps/AutohofsSwitch"
import { useFuelStations } from "@/hooks/useFuelStations"
import { useAutohofs } from "@/hooks/useAutohofs"
import { fuelStationCategories } from "@/lib/fuelStations"
import NavigatorMapOrders from "@/components/maps/NavigatorMapOrders"
import { selectAccountChatsAsList } from "@/reducers/accountChatsSlice"
import { selectRoutingProvider } from "@/reducers/serviceConfigSelectors"
import { resolveDriverJidForChat } from "@/lib/resolveDriverForChat"
import { STOP_NUMBERS_FLAG } from "@/lib/stopNumbers"
import PastStopsSwitch from "@/components/maps/PastStopsSwitch"
import { useFitWhenPastStopsShown } from "@/hooks/useFitWhenPastStopsShown"
import { selectCurrentOrderPastStops } from "@/lib/pastStops"
import { useDrivenTrail } from "@/hooks/useDrivenTrail"
import { selectLocation } from "@/reducers/locationsSelectors"

const SHOW_PAST_STOPS_STORAGE_KEY = "routeMap.showPastStops"

const readShowPastStops = () => {
  try {
    return localStorage.getItem(SHOW_PAST_STOPS_STORAGE_KEY) === "1"
  } catch {
    return false
  }
}

export type MapViewHandle = {
  getMapManager: () => MapManager | null
}

type MapViewProps = {
  sharedStops?: Route2g
  driverJid: string
  locationJid?: string
  isFullScreen?: boolean
  onOpenFullScreen?: () => void
  isSharedMap?: boolean
  isReadOnlyMap?: boolean
  isAnalyzeOrderMap?: boolean
  isAlertMap?: boolean
  /** Static preview: just the map + route, no controls / panels / CTAs.
   * Used by the route-summary widget in the Agent thread. */
  isSnapshot?: boolean
  hideRoutePanel?: boolean
  sharedRoute?: RouteInfo
  temporaryRoute?: Route2g
  temporaryRouteInfo?: RouteInfo
  temporaryAvoidOptions?: AvoidType[]
  temporaryLoadType?: LoadType[]
  temporaryCarConfig?: CarRouteConfig
  onTemporaryRouteUpdate?: (route: Route2g, diff: RouteStopDiff) => void
  onRouteDragChange?: (isDragging: boolean) => void
  mapViewRef?: Ref<MapViewHandle | null>
  children?: React.ReactNode
}

const MapView = ({
  mapViewRef,
  driverJid,
  locationJid,
  onOpenFullScreen,
  isFullScreen = false,
  isReadOnlyMap: isReadOnlyMapProp = false,
  isSharedMap = false,
  isAnalyzeOrderMap = false,
  isAlertMap = false,
  isSnapshot = false,
  hideRoutePanel = false,
  sharedRoute,
  sharedStops,
  temporaryRoute,
  temporaryRouteInfo,
  temporaryAvoidOptions,
  temporaryLoadType,
  temporaryCarConfig,
  onTemporaryRouteUpdate,
  onRouteDragChange,
  children,
}: MapViewProps) => {
  const { isDarkMode } = useDarkMode()
  // A snapshot is read-only and stripped of every overlay control.
  const isReadOnlyMap = isReadOnlyMapProp || isSnapshot
  const { upsertRoute2g, routeJid } = useContext(DriverContext)
  const onRouteStopsUpdate = useCallback(
    async (stops: Route2g["stops"]) => {
      if (isAnalyzeOrderMap && onTemporaryRouteUpdate && driverJid) {
        // For analyze order map, update temporary route
        // Use the current route from props or fallback to empty route structure
        const currentRoute = temporaryRoute || {
          stops: [],
          jid: driverJid,
          lastUpdatedBy: "",
          tolls: undefined,
        }
        const updatedRoute = {
          ...currentRoute,
          stops,
        }
        // Calculate diff between original and updated stops
        const diff = calculateRouteStopDiff(currentRoute.stops, stops)
        onTemporaryRouteUpdate(updatedRoute, diff)
      } else if (routeJid) {
        // For normal maps, update driver route
        await upsertRoute2g(
          {
            stops,
            jid: routeJid.toString(),
          },
          routeJid,
        )
      }
    },
    [
      routeJid,
      upsertRoute2g,
      isAnalyzeOrderMap,
      temporaryRoute,
      onTemporaryRouteUpdate,
      driverJid,
    ],
  )

  const isSmallScreen = useMediaQuery("(max-width: 900px)")

  // Stops - use temporary route for analyze order map, otherwise use from store
  const routeFromStore = useAppSelector(findRoute2g(driverJid))
  const routingProvider = useAppSelector(selectRoutingProvider)
  const route =
    isAnalyzeOrderMap && temporaryRoute ? temporaryRoute : routeFromStore

  const location = useDriverCurrentLocation(locationJid ?? driverJid)
  const currentActivity = useDriverCurrentActivity(driverJid)

  let currentLocation: [number, number] | undefined = location.currentLocation
  const currentLocationBearing = location.bearing
  if (isAnalyzeOrderMap && !routeJid) {
    currentLocation = undefined
  }

  // The past-stops overlay (completed/skipped stops and the driven trail) is
  // only for the live chat map of a driver.
  const pastStopsFlag = useFeatureFlagEnabled("map-past-stops")
  const pastStopsAllowed =
    Boolean(pastStopsFlag) &&
    !isSharedMap &&
    !isAlertMap &&
    !isSnapshot &&
    !isAnalyzeOrderMap
  const [showPastStops, setShowPastStops] = useState(readShowPastStops)
  const handleShowPastStopsChange = useCallback((checked: boolean) => {
    setShowPastStops(checked)
    try {
      localStorage.setItem(SHOW_PAST_STOPS_STORAGE_KEY, checked ? "1" : "0")
    } catch {
      // the preference just won't persist
    }
  }, [])
  const pastStopsView = useMemo(
    () => selectCurrentOrderPastStops(route),
    [route],
  )
  const pastStopsEnabled = pastStopsAllowed && showPastStops
  // useDriverCurrentLocation falls back to a fake point; the trail must only
  // grow from a position the driver really reported.
  const hasRealLocation =
    useAppSelector(selectLocation(locationJid ?? driverJid)) !== undefined
  const drivenTrail = useDrivenTrail({
    driverJid,
    orderTimestamp: pastStopsView?.orderTimestamp,
    enabled: pastStopsEnabled && pastStopsView !== null,
    currentLocation: hasRealLocation ? currentLocation : undefined,
    endsAt: pastStopsView?.trailEndsAt,
  })
  const pastStopsOverlay = useMemo<PastStopsOverlay | null>(
    () =>
      pastStopsEnabled && pastStopsView
        ? { stops: pastStopsView.stops, trail: drivenTrail }
        : null,
    [pastStopsEnabled, pastStopsView, drivenTrail],
  )
  const pastStopsOverlayHasContent =
    pastStopsOverlay !== null &&
    (pastStopsOverlay.stops.length > 0 || pastStopsOverlay.trail.length > 0)

  // Route with directions
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null)
  const dispatch = useAppDispatch()

  // Coordinate pinned via the "Go to coordinates" context-menu option
  const [pinnedCoordinate, setPinnedCoordinate] = useState<{
    lng: number
    lat: number
  } | null>(null)

  const handleShowCoordinate = useCallback((lng: number, lat: number) => {
    setPinnedCoordinate({ lng, lat })
    mapManagerRef.current?.flyToCoordinate(lng, lat)
  }, [])

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapManagerRef = useRef<MapManager | null>(null)

  // For showing entire route on initial load
  const initialRouteLoadedRef = useRef(false)

  const [currentMapStyle, setCurrentMapStyle] = useState<MapStyle>("default")
  const [fuelStationsVisible, setFuelStationsVisible] = useState(false)
  // Categorized stations are opt-in: nothing shows until the user enables
  // a category.
  const [visibleFuelStationCategories, setVisibleFuelStationCategories] =
    useState<string[]>([])
  // Only the shared map reads these.
  const sharedRouteSettings = useSharedRouteSettings()
  const shouldEnableFuelStations = !isSharedMap && !isAlertMap && !isSnapshot
  const fuelStations = useFuelStations(shouldEnableFuelStations)
  const availableFuelStationCategories = useMemo(
    () => fuelStationCategories(fuelStations),
    [fuelStations],
  )
  const [autohofsVisible, setAutohofsVisible] = useState(false)
  const autohofs = useAutohofs(shouldEnableFuelStations)
  const showRouteTraffic = isRouteTrafficMap({
    flagEnabled: useFeatureFlagEnabled(ROUTE_TRAFFIC_FLAG) === true,
    isSharedMap,
    isAlertMap,
    isSnapshot,
    isAnalyzeOrderMap,
  })
  const [trafficSettings, setTrafficSettings] = useRouteTrafficSettings()
  const showStopNumbers =
    useFeatureFlagEnabled(STOP_NUMBERS_FLAG) === true && !isAnalyzeOrderMap
  const navigatorAccountChat = useAppSelector((state) =>
    selectAccountChatsAsList(state).find(
      (accountChat) =>
        Boolean(accountChat.tmsCarId) &&
        (accountChat.jid === driverJid ||
          resolveDriverJidForChat(accountChat, accountChat.jid) === driverJid),
    ),
  )

  const handleChangeMapStyle = useCallback(
    (style: MapStyle) => {
      if (currentMapStyle !== style) {
        mapManagerRef.current?.changeMapStyle(style)
        setCurrentMapStyle(style)
      }
    },
    [currentMapStyle],
  )

  const handleShowEntireRoute = useCallback(() => {
    mapManagerRef.current?.showEntireRoute()
  }, [])

  // Callbacks for MapManager events
  const onNewRoute = useCallback((routeInfo: RouteInfo) => {
    setRouteInfo(routeInfo)
  }, [])

  const onClearRoute = useCallback(() => {
    setRouteInfo(null)
    dispatch(upsertRoute2gToll({ jid: driverJid, tolls: undefined }))
  }, [dispatch, driverJid])

  const onGetAvoidanceItems = useCallback(async (): Promise<
    AvoidanceItems | undefined
  > => {
    if (routeJid) {
      const result = await AdHocCommands.getAvoidanceItems(
        window.XMPP!,
        routeJid,
      )
      return AvoidanceItemsParser.parse(result)
    }
  }, [routeJid])

  const getMapManager = useCallback(() => mapManagerRef.current, [])

  useImperativeHandle(
    mapViewRef,
    () => ({
      getMapManager,
    }),
    [getMapManager],
  )

  useEffect(() => {
    mapManagerRef.current?.setTemporaryAvoidOptions(temporaryAvoidOptions)
    mapManagerRef.current?.setTemporaryLoadType(temporaryLoadType)
    mapManagerRef.current?.setTemporaryCarConfig(temporaryCarConfig)
  }, [temporaryLoadType, temporaryAvoidOptions, temporaryCarConfig])

  // When driverJid changes, we need to create a new map manager
  // Defer MapManager creation to the next frame
  useEffect(() => {
    initialRouteLoadedRef.current = false
    if (mapContainerRef.current) {
      const mapManager = new MapManager({
        mapConfig: {
          style: isSharedMap && !window.XMPP ? "americana" : "default",
          center: isSharedMap
            ? [20.0, 50.0]
            : (currentLocation ?? [20.0, 50.0]),
          zoom: isSharedMap ? 6 : 9,
        },
        isDarkMode,
        mapContainer: mapContainerRef.current,
        currentLocation,
        currentLocationBearing,
        currentActivity,
        isSharedMap,
        isAlertMap,
        isAnalyzeOrderMap,
        isReadOnlyMap,
        sharedRoute,
        sharedStops,
        routeJid: driverJid,
        onNewRoute,
        onClearRoute,
        onRouteStopsUpdate,
        onGetAvoidanceItems,
        routeInfo: isAnalyzeOrderMap ? temporaryRouteInfo : null,
        temporaryAvoidOptions,
        temporaryLoadType,
        temporaryCarConfig,
        shouldShowEntireRoute: !isAlertMap,
        routingProvider,
      })
      mapManagerRef.current = mapManager
    }
    return () => {
      // Free the WebGL context on unmount (e.g. a snapshot scrolled out of
      // view, or a widget-panel map tab closed) instead of leaking it.
      try {
        mapManagerRef.current?.clearMap()
      } catch {
        // best-effort teardown
      }
      mapManagerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (mapManagerRef.current && driverJid) {
      initialRouteLoadedRef.current = false
      mapManagerRef.current.updateMap({
        currentLocation,
        isDarkMode,
        routeJid: driverJid,
        onNewRoute,
        onClearRoute,
        onRouteStopsUpdate,
      })
    }
  }, [driverJid])

  // MapManager keeps the overlay across updateMap(), so depending on driverJid
  // makes a room switch replace (or clear) the previous room's overlay.
  useEffect(() => {
    mapManagerRef.current?.setPastStopsOverlay(pastStopsOverlay)
  }, [pastStopsOverlay, driverJid])

  // Setting the overlay doesn't move the map: bring the stops into view only
  // when the toggle goes on (or the room changes while it is on), never when
  // stops or trail points arrive later.
  const showEntireRoute = useCallback(
    () => mapManagerRef.current?.showEntireRoute(),
    [],
  )
  useFitWhenPastStopsShown({
    enabled: pastStopsEnabled,
    roomJid: driverJid,
    hasStops: (pastStopsOverlay?.stops.length ?? 0) > 0,
    fit: showEntireRoute,
  })

  useEffect(() => {
    if (mapManagerRef.current) {
      mapManagerRef.current.setOnRouteStopsUpdate(onRouteStopsUpdate)
    }
  }, [onRouteStopsUpdate])

  useEffect(() => {
    if (mapManagerRef.current) {
      mapManagerRef.current.setOnRouteDragChange(onRouteDragChange)
    }
  }, [onRouteDragChange])

  useEffect(() => {
    mapManagerRef.current?.setIsReadOnlyMap(isReadOnlyMap)
  }, [isReadOnlyMap])

  useEffect(() => {
    mapManagerRef.current?.setFuelStations(fuelStations)
  }, [fuelStations])

  useEffect(() => {
    mapManagerRef.current?.setFuelStationsVisibleCategories(
      visibleFuelStationCategories,
    )
  }, [visibleFuelStationCategories])

  useEffect(() => {
    mapManagerRef.current?.setAutohofs(autohofs)
  }, [autohofs])

  useEffect(() => {
    mapManagerRef.current?.setAutohofsVisible(
      shouldEnableFuelStations && autohofsVisible,
    )
  }, [autohofsVisible, shouldEnableFuelStations])

  useEffect(() => {
    // With categories, station visibility follows the selected layers; the
    // fuel button only opens the picker.
    const isVisible =
      availableFuelStationCategories.length > 0
        ? visibleFuelStationCategories.length > 0
        : fuelStationsVisible

    mapManagerRef.current?.setFuelStationsVisible(
      shouldEnableFuelStations && isVisible,
    )
  }, [
    fuelStationsVisible,
    shouldEnableFuelStations,
    availableFuelStationCategories,
    visibleFuelStationCategories,
  ])

  useEffect(() => {
    mapManagerRef.current?.setShowStopNumbers(showStopNumbers)
  }, [showStopNumbers])

  useEffect(() => {
    mapManagerRef.current?.setRouteTraffic(
      showRouteTraffic ? trafficSettings : ROUTE_TRAFFIC_OFF,
    )
  }, [showRouteTraffic, trafficSettings, routeInfo])

  const routeLegs = routeInfo?.routes?.[0]?.legs
  const trafficIncidentTypes = useMemo(
    () => countIncidentTypes(routeIncidents(routeLegs, new Date())),
    [routeLegs],
  )
  const trafficIncidentList = useMemo(
    () => incidentsAlongRoute(routeLegs, new Date()),
    [routeLegs],
  )
  const routeHasTraffic =
    trafficIncidentTypes.length > 0 ||
    !!routeLegs?.some((leg) => leg.traffic?.length)

  // Update current location
  useEffect(() => {
    if (mapManagerRef.current && currentLocation) {
      mapManagerRef.current.setCurrentLocation(
        currentLocation,
        currentActivity,
        currentLocationBearing,
      )
    }
  }, [currentLocation, currentLocationBearing, currentActivity])

  // Update if switched to dark mode
  useEffect(() => {
    if (
      mapManagerRef.current &&
      isDarkMode !== mapManagerRef.current.getIsDarkMode()
    ) {
      mapManagerRef.current.setIsDarkMode(isDarkMode)
    }
  }, [isDarkMode])

  useEffect(() => {
    if (isAnalyzeOrderMap && temporaryRouteInfo) {
      mapManagerRef.current?.setRouteInfo(temporaryRouteInfo)
      // Set the selected route label for colored route styling
      const routeLabel = temporaryRouteInfo.routes?.[0]?.label
      mapManagerRef.current?.setSelectedRouteLabel(routeLabel)
    } else if (isAnalyzeOrderMap && temporaryRouteInfo === undefined) {
      mapManagerRef.current?.clearRoute()
      mapManagerRef.current?.setSelectedRouteLabel(undefined)
    }
  }, [isAnalyzeOrderMap, temporaryRouteInfo])

  // Update stops in mapManager
  useEffect(() => {
    if (isAnalyzeOrderMap) {
      if (temporaryRoute) {
        const newPassthroughStopsCount =
          temporaryRoute.stops.filter((stop) => stop.action === "passthrough")
            .length || 0
        const currentPassthroughStopsCount =
          mapManagerRef.current
            ?.getStops()
            .filter((stop) => stop.action === "passthrough").length || 0
        const newStopsCount = temporaryRoute.stops.length
        const currentStopsCount = mapManagerRef.current?.getStops().length || 0
        mapManagerRef.current?.setShouldShowEntireRoute(
          newPassthroughStopsCount === currentPassthroughStopsCount,
        )
        mapManagerRef.current?.setRoute(temporaryRoute)
      } else {
        mapManagerRef.current?.clearRoute()
      }
    } else {
      if (route) {
        mapManagerRef.current?.setShouldShowEntireRoute(
          shouldShowEntireRouteOnUpdate(
            mapManagerRef.current.getStops(),
            route.stops,
          ),
        )

        mapManagerRef.current?.setRoute(route)
      } else {
        mapManagerRef.current?.clearRoute()
      }
    }
  }, [isAlertMap, isAnalyzeOrderMap, route, temporaryRoute])

  const tolls = useRouteTolls(driverJid, (route?.stops?.length ?? 0) > 0)

  // Fullscreen mode only for desktop and not for shared map
  const showFullScreenButton =
    !isMobile && !isSmallScreen && !isFullScreen && !isSharedMap && !isSnapshot

  // Reference route is not available for shared map
  const showReferenceRoute = !isSharedMap && !isAnalyzeOrderMap && !isSnapshot

  // Current location button is not available for shared map
  const showCurrentLocationButton = !isSharedMap && !isSnapshot

  // Close map button is only for mobile and not for the shared map.
  // It only dispatches hideMobileMap() for the chat's mobile map; on the
  // plan-order map it would be a dead "×" over the route.
  const showCloseMapButton =
    (isMobile || isSmallScreen) && !isSharedMap && !isAnalyzeOrderMap

  // In shared map and in fullscreen we have stops on left side not in scroll container
  const showScrollContainer =
    !isSharedMap && !isFullScreen && !isSnapshot && !hideRoutePanel

  // Share route button is visible only when there is route and not available for mobile or shared map
  const showShareRouteButton =
    !isSmallScreen &&
    !isMobile &&
    !isSharedMap &&
    !isFullScreen &&
    !isReadOnlyMap &&
    route &&
    (route?.stops?.length ?? 0) > 0

  // On mobile shared map we have read only stop list
  const showReadOnlyStopList =
    isSharedMap && !isAlertMap && (isMobile || isSmallScreen)

  const showNavigatorDetails =
    !isSharedMap &&
    !isAnalyzeOrderMap &&
    !isAlertMap &&
    !isSnapshot &&
    Boolean(navigatorAccountChat?.tmsCarId)

  useMapToaster(mapContainerRef.current?.parentElement)

  return (
    <MapContext.Provider
      value={{
        mapManager: mapManagerRef.current,
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height:
            isSharedMap && !isAlertMap && (isMobile || isSmallScreen)
              ? "calc(100% - 200px)"
              : "100%",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
          }}
          ref={mapContainerRef}
          id="mapContainer"
        >
          {showReferenceRoute && (
            <ReferenceRouteManager
              isReadOnlyMap={isReadOnlyMap}
              key={`${driverJid}_${mapManagerRef.current?.getCurrentMapStyle()}`}
              routeJid={driverJid}
              mapManager={mapManagerRef.current ?? null}
              hasCurrentRoute={!!routeInfo}
              currentRoute={route}
              hideDriverRoute={pastStopsOverlay !== null}
            />
          )}
        </div>
      </div>
      {showNavigatorDetails && (
        <NavigatorMapOrders
          key={`navigator_${driverJid}`}
          driverJid={driverJid}
        />
      )}
      <MapButtonColumn>
        {!isSnapshot && (
          <ChangeMapStyle
            className="relative"
            currentStyle={currentMapStyle}
            onChangeMapStyle={handleChangeMapStyle}
          />
        )}
        {!isSnapshot && (
          <ShowEntireRouteButton
            className="relative"
            onClick={handleShowEntireRoute}
            disabled={
              isSharedMap
                ? !sharedRoute
                : !routeInfo && !pastStopsOverlayHasContent
            }
          />
        )}
        {pastStopsAllowed && (
          <PastStopsSwitch
            className="relative"
            checked={showPastStops}
            disabled={pastStopsView === null}
            onCheckedChange={handleShowPastStopsChange}
          />
        )}
        {showCurrentLocationButton && (
          <ShowCurrentLocationButton
            className="relative"
            disabled={!currentLocation}
            onClick={() => mapManagerRef?.current?.showCurrentLocation()}
          />
        )}
        {shouldEnableFuelStations && fuelStations.length > 0 && (
          <FuelStationsSwitch
            className="relative"
            checked={fuelStationsVisible}
            onCheckedChange={setFuelStationsVisible}
            categories={availableFuelStationCategories}
            visibleCategories={visibleFuelStationCategories}
            onVisibleCategoriesChange={setVisibleFuelStationCategories}
          />
        )}
        {shouldEnableFuelStations && autohofs.length > 0 && (
          <AutohofsSwitch
            className="relative"
            checked={autohofsVisible}
            onCheckedChange={setAutohofsVisible}
          />
        )}
        {showRouteTraffic && routeHasTraffic && (
          <RouteTrafficSwitch
            className="relative"
            settings={trafficSettings}
            onSettingsChange={setTrafficSettings}
            incidentTypes={trafficIncidentTypes}
            trafficFetchedAt={routeInfo?.traffic_fetched_at}
            incidents={trafficIncidentList}
            onShowIncident={(incident) =>
              mapManagerRef.current?.showTrafficIncident(incident)
            }
          />
        )}
      </MapButtonColumn>
      {showCloseMapButton && <CloseMobileMap />}
      {showFullScreenButton && <OpenFullScreen onClick={onOpenFullScreen} />}
      {showScrollContainer && (
        <ScrollContainer>
          <div>
            {!isReadOnlyMap && (
              <RouteOptionsSelect
                stops={route?.stops}
                driverJid={driverJid}
                onTollsToggle={tolls.toggle}
                tollsToggleLabel={tolls.label}
              />
            )}
            {!isReadOnlyMap && tolls.visible && (
              <TollsInformation tolls={tolls.tolls} isSharedMap={isSharedMap} />
            )}
            <TemperatureAlertSettings driverJid={driverJid} key={driverJid} />
            {isSltExportEnabledVhost(window.XMPP?.jid?.domain) && (
              <DeliveryOrderExportStatusCard
                driverJid={driverJid}
                stops={route?.stops}
              />
            )}
            <RouteStopList
              isReadOnlyMap={isReadOnlyMap}
              driverJid={driverJid}
              locationJid={locationJid}
            />
            {showShareRouteButton && (
              <ShareRoute route={route} driverJid={driverJid} />
            )}
          </div>
        </ScrollContainer>
      )}
      {showReadOnlyStopList && (
        <div className={css.sharedMobileMap}>
          <ReadOnlyRouteStopList
            routeStops={sharedStops?.stops}
            showLegDistance={sharedRouteSettings.showLegDistance}
            legs={sharedRoute?.routes?.[0]?.legs}
          />
        </div>
      )}
      {isAlertMap && (
        <WaypointDetailsMenu mapManagerInstance={mapManagerRef.current} />
      )}
      {!isSharedMap &&
        !isReadOnlyMap &&
        (route?.stops || isAnalyzeOrderMap) && (
          <>
            <WaypointContextMenu
              mapManagerInstance={mapManagerRef.current}
              onShowCoordinate={handleShowCoordinate}
            />
            <WaypointDetailsMenu mapManagerInstance={mapManagerRef.current} />
          </>
        )}
      {pinnedCoordinate && (
        <Marker
          longitude={pinnedCoordinate.lng}
          latitude={pinnedCoordinate.lat}
          onClick={() => setPinnedCoordinate(null)}
          style={{ cursor: "pointer" }}
        />
      )}
      {children}
    </MapContext.Provider>
  )
}
export default memo(MapView)
